import {
  ApostilleTransaction,
  HashingType,
  IApostilleMetadata,
  IApostilleOptions,
  MetadataKeyHelper,
} from 'simple-apostille-v2';
import {
  Account,
  AccountMetadataTransaction,
  Address,
  AggregateTransaction,
  Metadata,
  MetadataHttp,
  MetadataSearchCriteria,
  MultisigHttp,
  TransactionGroup,
  TransactionHttp,
} from 'symbol-sdk';
import { format } from 'date-fns';
import AppConfig from './AppConfig';
import IMetadata from '../models/AIMetadata';
import AMetadata from '../models/AMetadata';

export default class ApostilleService {
  public static async createAnnounceInfo(
    hashedData: string,
    fileName: string,
    singerAccount: Account,
    assignOwners: Address[],
    metadatas: IMetadata[],
  ) {
    const apiEndpoint = AppConfig.apiEndpoint();
    const generationHash = AppConfig.generationHash();
    const feeMultiplier = AppConfig.feeMultiplier();
    const epochAdjustment = AppConfig.epochAdjustment();

    const options = this.createOptions(assignOwners, metadatas);

    if (apiEndpoint && generationHash && feeMultiplier) {
      const apostilleTx = ApostilleTransaction.createFromHashedData(
        hashedData,
        HashingType.Type.sha256,
        fileName,
        singerAccount,
        AppConfig.networkType(),
        generationHash,
        feeMultiplier,
        apiEndpoint,
        epochAdjustment,
        options,
      );

      const announceInfo = await apostilleTx.singedTransactionAndAnnounceType();
      return announceInfo;
    }
    return null;
  }

  private static createOptions(assignOwners: Address[], metadatas: IMetadata[]) {
    const options: IApostilleOptions = {};
    if (assignOwners.length) {
      options.assignOwners = assignOwners;
    }

    if (metadatas.length) {
      const md: IApostilleMetadata = {};
      metadatas.forEach((m) => {
        switch (m.key) {
          case 'author':
            md.author = m.value;
            break;
          case 'description':
            md.description = m.value;
            break;
          case 'originUrl':
            md.originUrl = m.value;
            break;
          case 'tag':
            md.tag = m.value;
            break;
          case 'title':
            md.title = m.value;
            break;
          default:
            break;
        }
      });
      options.metadata = md;
    }

    return options;
  }

  public static createApostilleFileName(filename: string, txHash: string) {
    const splitFilename = this.splitExt(filename);
    const date = format(new Date(), 'yyyy-MM-dd');
    return `${splitFilename[0]} -- Apostille TX ${txHash} -- ${date}.${splitFilename[1]}`;
  }

  private static splitExt(filename: string) {
    return filename.split(/\.(?=[^.]+$)/);
  }

  public static async getMultisigAccountCosigner(address: Address) {
    const cosignatories: Address[] = [];
    const apiEndpoint = AppConfig.apiEndpoint();
    try {
      if (apiEndpoint) {
        const multisigHttp = new MultisigHttp(apiEndpoint);
        const result = await multisigHttp
          .getMultisigAccountInfo(address)
          .toPromise();
        return result.cosignatoryAddresses;
      }
      return [];
    } catch (err) {
      console.error(err);
      return cosignatories;
    }
  }

  public static async getMetadata(address: Address) {
    const apiEndpoint = AppConfig.apiEndpoint();
    const metadatas: AMetadata[] = [];
    try {
      if (apiEndpoint) {
        const metadataHttp = new MetadataHttp(apiEndpoint);
        const criteria: MetadataSearchCriteria = {
          sourceAddress: address,
        };
        const result = await metadataHttp.search(criteria).toPromise();
        result.data.forEach((m: Metadata) => {
          const key = MetadataKeyHelper.getKeyNameByKeyId(
            m.metadataEntry.scopedMetadataKey,
          );

          const metadata = AMetadata.createFromKeyAndValue(
            key,
            m.metadataEntry.value,
          );
          metadatas.push(metadata);
        });
      }
      return metadatas;
    } catch (err) {
      console.error(err);
      return metadatas;
    }
  }

  public static async getMetadataFromHash(hash: string) {
    const apiEndpoint = AppConfig.apiEndpoint();
    const metadatas: AMetadata[] = [];

    try {
      const txHttp = new TransactionHttp(apiEndpoint);
      txHttp.getTransaction(hash, TransactionGroup.Partial).subscribe((tx) => {
        const agtx = tx as AggregateTransaction;
        const mttxs = agtx.innerTransactions.filter(
          (t) => t instanceof AccountMetadataTransaction,
        ) as AccountMetadataTransaction[];
        const m = mttxs.forEach((x) => {
          const metadata = AMetadata.createFromKeyAndValue(
            MetadataKeyHelper.getKeyNameByKeyId(x.scopedMetadataKey), x.value,
          );
          metadatas.push(metadata);
        });
      });
    } catch (err) {
      console.log(err);
    }
    return metadatas;
  }
}
